import { Loader2, Maximize, Pause, Play, Volume2, VolumeX } from "lucide-react";
import { MouseEvent, useEffect, useRef, useState } from "react";
import { cn } from "../../utils/classes";
import styles from "./video.module.css";

export type VideoPreviewProps = { file: File; className?: string };

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds)) return "0:00";
  const total = Math.floor(seconds);
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  return `${minutes}:${String(secs).padStart(2, "0")}`;
}

/**
 * Video branch of the shared `FilePreview` dispatcher (`./index.tsx`) —
 * same object-URL-from-a-File pattern `ImagePreview` uses. Styled with CSS
 * Modules (see `video.module.css`'s own doc comment for why, unlike this
 * folder's other Tailwind-based previews).
 *
 * Deliberately NOT the browser's native `<video controls>` bar — that reads
 * as generic OS chrome, not "PCG design". Owns its own play/pause, seek
 * scrubber, time display, mute, and fullscreen, all custom-styled — the
 * native element only supplies decoding/playback, `controls` is left off.
 */
export function VideoPreview({ file, className }: VideoPreviewProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  if (!src) {
    return (
      <div className={cn(styles.loading, className)}>
        <Loader2 className={styles.spinner} aria-hidden="true" />
      </div>
    );
  }

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) video.play();
    else video.pause();
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
  };

  const toggleFullscreen = () => {
    frameRef.current?.requestFullscreen?.();
  };

  const seekToEvent = (event: MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    if (!video || duration <= 0) return;
    const track = event.currentTarget;
    const rect = track.getBoundingClientRect();
    const fraction = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    video.currentTime = fraction * duration;
  };

  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div ref={frameRef} className={cn(styles.frame, className)}>
      <video
        ref={videoRef}
        src={src}
        className={styles.video}
        onClick={togglePlay}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
      />

      {!playing && (
        <button
          type="button"
          aria-label={`Play ${file.name}`}
          onClick={togglePlay}
          className={styles.playOverlay}
        >
          <span className={styles.playButton}>
            <Play className={styles.playIcon} fill="currentColor" aria-hidden="true" />
          </span>
        </button>
      )}

      <div className={styles.controlsBar}>
        <button
          type="button"
          onClick={togglePlay}
          aria-label={playing ? "Pause" : "Play"}
          className={styles.iconButton}
        >
          {playing ? (
            <Pause className={styles.controlIcon} fill="currentColor" aria-hidden="true" />
          ) : (
            <Play className={styles.controlIcon} fill="currentColor" aria-hidden="true" />
          )}
        </button>

        <span className={styles.timeText}>{formatTime(currentTime)}</span>

        <div
          className={styles.progressTrack}
          onClick={seekToEvent}
          role="slider"
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={duration}
          aria-valuenow={currentTime}
        >
          <div className={styles.progressFill} style={{ width: `${progressPct}%` }} />
          <div className={styles.progressThumb} style={{ left: `${progressPct}%` }} />
        </div>

        <span className={styles.timeText}>{formatTime(duration)}</span>

        <button
          type="button"
          onClick={toggleMute}
          aria-label={muted ? "Unmute" : "Mute"}
          className={styles.iconButton}
        >
          {muted ? (
            <VolumeX className={styles.controlIcon} aria-hidden="true" />
          ) : (
            <Volume2 className={styles.controlIcon} aria-hidden="true" />
          )}
        </button>

        <button
          type="button"
          onClick={toggleFullscreen}
          aria-label="Fullscreen"
          className={styles.iconButton}
        >
          <Maximize className={styles.controlIcon} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
